import React, {useCallback, useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import {LoadingData} from '../../../../components/LoadingData'
import {SavControleItem, SavDetailResponse} from '../../../../components/sav/Models'
import {getSavControles, getSavDossier, savErreur} from '../../../../components/sav/requests'
import {
  formatSavDate,
  formatSavMontant,
  nomSavClient,
  SAV_ORIGINES,
} from '../../../../components/sav/libelles'
import SavStatutBadge from '../components/SavStatutBadge'
import SavAc10 from '../components/SavAc10'
import SavDocuments from '../components/SavDocuments'
import SavGarantie from '../components/SavGarantie'
import SavControleForm from '../components/SavControleForm'
import {useSavReferentiels} from '../components/useSavReferentiels'
import {SavInformerClientModal} from '../components/SavInformerClient'

const CLE_SITE = 'sav.controle.site'

const lireSite = (): string => {
  try {
    return localStorage.getItem(CLE_SITE) ?? ''
  } catch {
    return ''
  }
}

const echeance = (item: SavControleItem) => {
  const date = item.dossier.dateEcheance
  if (!date) return {texte: '', retard: false}
  if (item.dossier.enRetard) {
    const jours = Math.max(1, Math.floor((Date.now() - new Date(date).getTime()) / 86400000))
    return {texte: `En retard de ${jours} j`, retard: true}
  }
  return {texte: `Avant le ${formatSavDate(date)}`, retard: false}
}

// Écran 6 : contrôle labo, pièce par pièce, sur un site
const SavControlePage = () => {
  const navigate = useNavigate()
  const referentiels = useSavReferentiels()
  const [site, setSite] = useState<string>(lireSite())
  const [file, setFile] = useState<Array<SavControleItem> | null>(null)
  const [choisie, setChoisie] = useState<number | null>(null)
  const [detail, setDetail] = useState<SavDetailResponse | null>(null)
  // Dernière pièce validée : message proposé au client (non fondé…), sur le dossier tel qu'il est après validation
  const [message, setMessage] = useState<SavDetailResponse | null>(null)

  const charger = useCallback(
    (apresValidation?: number) =>
      getSavControles(site ? Number(site) : null)
        .then((liste) => {
          setFile(liste)
          // Après une validation, la pièce suivante de la file est sélectionnée
          setChoisie((courante) => {
            if (apresValidation === undefined && liste.some((i) => i.ligne.id === courante))
              return courante
            return liste[apresValidation ?? 0]?.ligne.id ?? liste[0]?.ligne.id ?? null
          })
        })
        .catch((e) => toast.error(savErreur(e))),
    [site]
  )

  useEffect(() => {
    charger()
    try {
      localStorage.setItem(CLE_SITE, site)
    } catch {
      // stockage indisponible : le site reste choisi pour la session
    }
  }, [charger, site])

  const item = file?.find((i) => i.ligne.id === choisie) ?? null

  useEffect(() => {
    setDetail(null)
    if (item) {
      getSavDossier(item.dossier.id)
        .then(setDetail)
        .catch((e) => toast.error(savErreur(e)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.ligne.id])

  if (!file || !referentiels) return <LoadingData />

  const ligne = detail?.dossier.lignes.find((l) => l.id === choisie)
  const retour = detail?.dossier.retours.filter((r) => r.dateReception).slice(-1)[0]
  const photosClient =
    detail?.dossier.documents.filter(
      (d) => d.type === 'PHOTO_CLIENT' && (!d.ligne || d.ligne.id === choisie)
    ) ?? []
  const photosControle =
    detail?.dossier.documents.filter(
      (d) => d.type === 'PHOTO_CONTROLE' && d.ligne?.id === choisie
    ) ?? []

  return (
    <>
      <div className='mb-4'>
        <SavAc10 etape='Retour SAV' acteur='Contrôleur labo' delai='J + 1' />
      </div>
      <div className='row g-5'>
        <div className='col-lg-4'>
          <div className='card'>
            <div className='card-header align-items-center'>
              <h3 className='card-title'>Pièces à contrôler ({file.length})</h3>
              <select
                className='form-select form-select-sm w-auto'
                value={site}
                onChange={(e) => setSite(e.target.value)}
              >
                <option value=''>Tous mes sites</option>
                {referentiels.sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    Site {s.nom}
                  </option>
                ))}
              </select>
            </div>
            <div
              className='card-body p-0'
              style={{maxHeight: 'calc(100vh - 260px)', overflowY: 'auto'}}
            >
              {file.length ? (
                file.map((i) => {
                  const e = echeance(i)
                  const active = i.ligne.id === choisie
                  return (
                    <button
                      key={i.ligne.id}
                      className='btn text-start w-100 rounded-0 border-bottom px-5 py-4'
                      style={{
                        backgroundColor: active ? '#FFFDF0' : 'transparent',
                        borderLeft: `4px solid ${active ? '#50CD89' : 'transparent'}`,
                      }}
                      onClick={() => setChoisie(i.ligne.id)}
                    >
                      <div className='d-flex justify-content-between'>
                        <span className='fw-bolder'>{i.dossier.numero}</span>
                        <span
                          className={e.retard ? 'text-danger fw-bolder fs-7' : 'text-muted fs-7'}
                        >
                          {e.texte}
                        </span>
                      </div>
                      <div>
                        {(i.ligne.pieceEntre?.code ?? i.ligne.codePieceExterne) &&
                          `${i.ligne.pieceEntre?.code ?? i.ligne.codePieceExterne} - `}
                        {i.ligne.libellePiece}
                      </div>
                      <div className='text-muted fs-7'>
                        {nomSavClient(i.dossier.client, i.dossier.contactNom)} · reçu le{' '}
                        {formatSavDate(i.dateReception)}
                      </div>
                    </button>
                  )
                })
              ) : (
                <div className='p-5 text-muted'>Aucune pièce à contrôler.</div>
              )}
            </div>
          </div>
        </div>

        <div className='col-lg-8'>
          {!item ? (
            <div className='card'>
              <div className='card-body text-muted'>Sélectionnez une pièce.</div>
            </div>
          ) : !detail || !ligne ? (
            <LoadingData />
          ) : (
            <div className='card'>
              <div className='card-body'>
                <div className='d-flex justify-content-between flex-wrap gap-2'>
                  <div>
                    <div className='fs-3 fw-bolder text-uppercase'>
                      {(ligne.pieceEntre?.code ?? ligne.codePieceExterne) &&
                        `${ligne.pieceEntre?.code ?? ligne.codePieceExterne} - `}
                      {ligne.libellePiece}
                    </div>
                    <div className='text-muted'>
                      SAV #{detail.dossier.numero} ·{' '}
                      {nomSavClient(detail.dossier.client, detail.dossier.contactNom)} ·{' '}
                      {SAV_ORIGINES[detail.dossier.origine]}
                      {ligne.referenceConstructeur && ` · Réf. OE ${ligne.referenceConstructeur}`}
                    </div>
                  </div>
                  <div className='d-flex gap-2 align-items-start'>
                    <span
                      className={`badge ${
                        ligne.pieceEntre ? 'badge-light-success' : 'badge-light-warning'
                      }`}
                    >
                      {ligne.pieceEntre ? 'Pièce reconnue dans KOENA' : 'Pièce hors KOENA'}
                    </span>
                    <SavStatutBadge code={detail.dossier.statut.code} />
                  </div>
                </div>

                <div className='row g-3 my-4'>
                  <div className='col-md-3'>
                    <div className='text-muted fs-7'>Motif</div>
                    <div className='fw-semibold'>{ligne.motif?.libelle}</div>
                  </div>
                  <div className='col-md-3'>
                    <div className='text-muted fs-7'>Prix de vente HT</div>
                    <div className='fw-semibold'>
                      {formatSavMontant(ligne.prixVenteHt)}{' '}
                      <span className='text-muted fs-8'>
                        ({ligne.prixVerifie ? 'vérifié' : 'déclaré'})
                      </span>
                    </div>
                  </div>
                  <div className='col-md-3'>
                    <div className='text-muted fs-7'>Garantie</div>
                    <SavGarantie ligne={ligne} declarative={detail.garantieDeclarative} />
                  </div>
                  <div className='col-md-3'>
                    <div className='text-muted fs-7'>Réception</div>
                    <div className='fw-semibold'>{formatSavDate(retour?.dateReception, true)}</div>
                    {retour &&
                      (retour.reserveColisAbime ||
                        retour.reservePieceManquante ||
                        retour.reservePieceDifferente ||
                        retour.reserveReception) && (
                        <div className='text-danger fs-8'>
                          Réserves :{' '}
                          {[
                            retour.reserveColisAbime && 'colis abîmé',
                            retour.reservePieceManquante && 'pièce manquante',
                            retour.reservePieceDifferente && 'pièce différente',
                            retour.reserveReception,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </div>
                      )}
                  </div>
                </div>

                <div className='text-muted fw-bold mb-1'>Réclamation du client</div>
                <div className='bg-light rounded p-4 mb-4' style={{whiteSpace: 'pre-line'}}>
                  {detail.dossier.description}
                </div>
                {photosClient.length > 0 && (
                  <div className='mb-4'>
                    <div className='text-muted fw-bold mb-1'>Photos client</div>
                    <SavDocuments documents={photosClient} />
                  </div>
                )}

                <SavControleForm
                  key={ligne.id}
                  dossierId={detail.dossier.id}
                  ligne={ligne}
                  referentiels={referentiels}
                  onMaj={setDetail}
                  onValidee={(d) => {
                    if (d.messagePropose) setMessage(d)
                    charger(file.findIndex((i) => i.ligne.id === choisie))
                  }}
                />
                {photosControle.length > 0 && (
                  <div className='mt-4'>
                    <div className='text-muted fw-bold mb-1'>Photos de contrôle</div>
                    <SavDocuments documents={photosControle} />
                  </div>
                )}

                <div className='d-flex justify-content-end mt-5'>
                  <button
                    className='btn btn-light-primary'
                    onClick={() => navigate(`/sav/dossiers/${detail.dossier.id}/fiche`)}
                  >
                    Ouvrir le dossier
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {message?.messagePropose && (
        <SavInformerClientModal
          dossier={message.dossier}
          evenement={message.messagePropose}
          onClose={() => setMessage(null)}
        />
      )}
    </>
  )
}

export default SavControlePage
